'use client';

import ControlledButton from '@umodeapporg/ui/components/Form/ControlledButton';
import ControlledTooltip from '@umodeapporg/ui/components/UI/ControlledTooltip';
import { MaterialSymbol } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import { usePathname } from 'next/navigation';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AjudaDaTelaPainel } from '@/app/ajuda/components/AjudaDaTelaPainel';
import { buildDuvidas, buildTelas } from '@/app/ajuda/helpers/ajudaConteudo';
import { telaDaRota } from '@/app/ajuda/helpers/ajudaDaRota';
import { duvidasDaTela } from '@/app/ajuda/helpers/ajudaTarefas';
import { ACTIVE_TAB_PARAM } from '@/domain/common/hooks/useActiveTabParam';
import { useUrlQueryState } from '@/domain/common/hooks/useUrlQueryState';

/**
 * O "?" do cabeçalho: o capítulo DESTA tela no manual, ao lado dela.
 *
 * A Ajuda só servia a quem lembrava de sair da tela e ir procurá-la, e quem está travado
 * numa tela não faz esse caminho. O botão abria o manual em OUTRA página, o que resolvia
 * metade: a pessoa lia a resposta longe da tela onde ia aplicá-la, e voltava procurando
 * onde tinha parado. Agora ele abre um painel ao lado, e o manual inteiro continua a um
 * clique dentro dele.
 *
 * Ele mora no `PageTitle` do produto, e não em cada página, porque toda rota usa esse
 * cabeçalho: assim nenhuma tela nasce sem o "?" por esquecimento, e uma rota que o manual
 * não descreve (a própria Ajuda, uma rota de detalhe) simplesmente não ganha botão.
 */
export function AjudaDaTela() {
  const { t } = useTranslation('common');
  const pathname = usePathname();
  const { getParam } = useUrlQueryState();
  const [aberto, setAberto] = useState(false);

  const telas = useMemo(() => buildTelas(t), [t]);
  const duvidas = useMemo(() => buildDuvidas(t), [t]);
  const abaPedida = getParam(ACTIVE_TAB_PARAM);
  const tela = telaDaRota(telas, pathname ?? '', abaPedida === '' ? null : abaPedida);

  if (tela === null) {
    return null;
  }

  const rotulo = t('ajuda.daTela.abrir', { tela: tela.nome });

  return (
    <>
      <ControlledTooltip content={rotulo}>
        <ControlledButton
          type="button"
          variant="text"
          tone="neutral"
          size="sm"
          aria-label={rotulo}
          aria-expanded={aberto}
          onClick={() => setAberto(true)}
          className="shrink-0 rounded-full text-foreground-subtle hover:text-brand-emphasis"
        >
          <MaterialSymbol icon="help" size={20} />
        </ControlledButton>
      </ControlledTooltip>

      <AjudaDaTelaPainel
        tela={tela}
        duvidas={duvidasDaTela(duvidas, tela.id)}
        aberto={aberto}
        onClose={() => setAberto(false)}
      />
    </>
  );
}
